/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   pipelineMonitoringApi.ts
 *     Feature         :   206-pipeline-monitoring-redesign
 *     Description     :   API client for /api/pipeline-monitoring/* (the universal-logging-
 *                         driven monitoring redesign). All requests go through the
 *                         centralized apiClient (MSAL token interceptor).
 *     Constitution    :   Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-30
 * =========================================================================================
 */
import { apiClient } from './apiClient';
import type {
  DashboardResponse, ActivitiesQuery, ActivitiesResponse, FilterOptions,
  RunDetailResponse, AppsResponse, PredictabilityResponse,
  CurationListResponse, NewArrivalsResponse, CurationRow, CurationPatch,
  CurationAuditEntry, ImportanceTier,
} from '@shared/types/pipelineMonitoring';

const BASE = '/api/pipeline-monitoring';

export async function fetchDashboard(window: string): Promise<DashboardResponse> {
  const { data } = await apiClient.get<DashboardResponse>(`${BASE}/dashboard?window=${encodeURIComponent(window)}`);
  return data;
}

export async function fetchActivities(query: ActivitiesQuery): Promise<ActivitiesResponse> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') { qs.set(key, String(value)); }
  }
  const q = qs.toString();
  const { data } = await apiClient.get<ActivitiesResponse>(`${BASE}/activities${q ? `?${q}` : ''}`);
  return data;
}

/**
 * The apps dropdown is always the full, unscoped catalog (the backend `getFilterOptions`
 * ignores the context). The status-facet counts, however, are scoped to the caller's
 * context (app + window + search + retired) so the chips reflect the pipeline in view.
 */
export interface FilterOptionsContext {
  app?: string;
  from?: string;
  q?: string;
  includeRetired?: boolean;
}
export async function fetchFilterOptions(ctx: FilterOptionsContext = {}): Promise<FilterOptions> {
  const qs = new URLSearchParams();
  if (ctx.app) qs.set('app', ctx.app);
  if (ctx.from) qs.set('from', ctx.from);
  if (ctx.q) qs.set('q', ctx.q);
  if (ctx.includeRetired) qs.set('includeRetired', 'true');
  const s = qs.toString();
  const { data } = await apiClient.get<FilterOptions>(`${BASE}/filter-options${s ? `?${s}` : ''}`);
  return data;
}

export async function fetchApps(): Promise<AppsResponse> {
  const { data } = await apiClient.get<AppsResponse>(`${BASE}/apps`);
  return data;
}

export async function fetchPredictability(): Promise<PredictabilityResponse> {
  const { data } = await apiClient.get<PredictabilityResponse>(`${BASE}/predictability`);
  return data;
}

/** Kill-switch flag (pipeline_monitoring_v2_enabled). */
export async function fetchMonitoringConfig(): Promise<{ v2Enabled: boolean }> {
  const { data } = await apiClient.get<{ v2Enabled: boolean }>(`${BASE}/config`);
  return data;
}

export async function fetchRunDetail(runId: string): Promise<RunDetailResponse> {
  const { data } = await apiClient.get<RunDetailResponse>(`${BASE}/runs/${encodeURIComponent(runId)}`);
  return data;
}

// ── Spec 254 — curation (Management surface) ────────────────────────────────
export async function fetchCuration(showRetired: boolean): Promise<CurationListResponse> {
  const { data } = await apiClient.get<CurationListResponse>(`${BASE}/curation?showRetired=${showRetired ? 'true' : 'false'}`);
  return data;
}

export async function fetchNewArrivals(): Promise<NewArrivalsResponse> {
  const { data } = await apiClient.get<NewArrivalsResponse>(`${BASE}/new-arrivals`);
  return data;
}

export async function fetchCurationAudit(appInstance: string): Promise<{ appInstance: string; entries: CurationAuditEntry[] }> {
  const { data } = await apiClient.get<{ appInstance: string; entries: CurationAuditEntry[] }>(`${BASE}/curation/${encodeURIComponent(appInstance)}/audit`);
  return data;
}

export async function patchCuration(appInstance: string, patch: CurationPatch): Promise<CurationRow> {
  const { data } = await apiClient.patch<CurationRow>(`${BASE}/curation/${encodeURIComponent(appInstance)}`, patch);
  return data;
}

export async function bulkTriage(appInstances: string[], action: 'adopt' | 'retire', importanceTier?: ImportanceTier): Promise<{ updated: number; failed: Array<{ appInstance: string; reason: string }> }> {
  const { data } = await apiClient.post<{ updated: number; failed: Array<{ appInstance: string; reason: string }> }>(`${BASE}/curation/bulk-triage`, { appInstances, action, importanceTier });
  return data;
}

export async function linkIdentity(appInstance: string, priorAppInstance: string): Promise<void> {
  await apiClient.post(`${BASE}/curation/${encodeURIComponent(appInstance)}/identity-link`, { priorAppInstance });
}

export async function unlinkIdentity(appInstance: string): Promise<void> {
  await apiClient.delete(`${BASE}/curation/${encodeURIComponent(appInstance)}/identity-link`);
}

/** Export the current filtered run set as CSV (diagnostics tier — the backend enforces). */
export async function exportRuns(params: Record<string, string>): Promise<void> {
  const qs = new URLSearchParams({ ...params, format: 'csv' }).toString();
  const res = await apiClient.get(`${BASE}/activities/export?${qs}`, { responseType: 'blob' });
  const blob = new Blob([res.data as BlobPart], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'pipeline-runs.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Download the per-run debug bundle (US4) — via apiClient so the MSAL token is attached. */
export async function downloadDebugBundle(runId: string): Promise<void> {
  const res = await apiClient.get(`${BASE}/runs/${encodeURIComponent(runId)}/bundle`, { responseType: 'blob' });
  const blob = new Blob([res.data as BlobPart], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `debug-bundle_${runId}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
